import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Holidays from 'date-holidays';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCountryConfig } from '../../components/Config/useCountryConfig';

const getDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildHolidayMap = (countryCode, year, language) => {
  const holidayMap = new Map();

  try {
    const hd = new Holidays(countryCode.toUpperCase(), { languages: [language, 'en'] });
    const holidays = hd.getHolidays(year) || [];

    holidays.forEach((holiday) => {
      const holidayDate = holiday.date ? new Date(holiday.date) : null;

      if (!holidayDate || Number.isNaN(holidayDate.getTime())) {
        return;
      }

      holidayMap.set(getDateKey(holidayDate), holiday);
    });
  } catch (error) {
    console.warn('Unable to load holidays for calendar', error);
  }

  return holidayMap;
};

const buildCalendarCells = (date, holidayMaps) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  let leadingDays = firstDayOfMonth.getDay() - 1;

  if (leadingDays < 0) {
    leadingDays = 6;
  }

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = new Date(year, month, 1 - leadingDays + index);
    const cellYear = cellDate.getFullYear();
    const holiday = holidayMaps[cellYear]?.get(getDateKey(cellDate)) || null;
    const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;

    return {
      date: cellDate,
      day: cellDate.getDate(),
      isCurrentMonth: cellDate.getMonth() === month,
      isWeekend,
      isHoliday: Boolean(holiday),
      holidayName: holiday?.name || '',
      isToday: getDateKey(cellDate) === getDateKey(new Date())
    };
  });
};

// Mois local 'YYYY-MM' (toISOString passe en UTC et décale le 1er du mois).
export const toYearMonth = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

// month ('YYYY-MM') + onMonthChange : le parent pilote le mois affiché, comme le
// tableau de bord qui suit le mois du calendrier (Yros/payor_enterprise#263).
// Sans ces props, le calendrier garde son propre mois.
const MyCalendar = ({ month, onMonthChange } = {}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
  const monthNames = t('myCalendar.monthNames', { returnObjects: true });
  const dayNames = t('myCalendar.dayNames', { returnObjects: true });
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(dateLocale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }), [dateLocale]);
  const [ownDate, setOwnDate] = useState(new Date());
  const isControlled = Boolean(month);
  const currentDate = useMemo(() => {
    if (!isControlled) return ownDate;
    const [year, monthNumber] = month.split('-').map(Number);
    return new Date(year, monthNumber - 1, 1);
  }, [isControlled, month, ownDate]);
  const countryConfig = useCountryConfig();

  const countryCode = countryConfig?.taxSystem || 'sn';
  const countryLabel = typeof window !== 'undefined'
    ? localStorage.getItem('selectedCountryLabel') || countryCode.toUpperCase()
    : countryCode.toUpperCase();

  const holidayMaps = useMemo(() => {
    const years = [currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1];

    return years.reduce((accumulator, year) => {
      accumulator[year] = buildHolidayMap(countryCode, year, i18n.resolvedLanguage);
      return accumulator;
    }, {});
  }, [countryCode, currentDate, i18n.resolvedLanguage]);

  const navigateMonth = (direction) => {
    // Le 1er du mois : depuis un 31, setMonth(+1) sautait un mois court.
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1);
    if (onMonthChange) onMonthChange(toYearMonth(newDate));
    if (!isControlled) setOwnDate(newDate);
  };

  const days = buildCalendarCells(currentDate, holidayMaps);
  const monthYear = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  const legendItems = [
    { label: t('myCalendar.weekend'), className: 'bg-rose-100 text-rose-700 border-rose-200' },
    { label: t('myCalendar.holiday'), className: 'bg-amber-200 text-amber-900 border-amber-300' },
    { label: t('myCalendar.today'), className: 'bg-[#0C5B6D] text-white border-[#0C5B6D]' }
  ];

  return (
    <div className="max-w-md w-full mx-auto font-sans px-2 pb-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-3">
        <button
          onClick={() => navigateMonth(-1)}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-[#0C5B6D]/10 flex items-center justify-center transition-colors"
          aria-label={t('myCalendar.previousMonth')}
        >
          <ChevronLeft className="w-5 h-5 text-[#0C5B6D]" />
        </button>
        
        <h2 className="text-lg sm:text-xl font-semibold text-[#0C5B6D] tracking-wide">
          {monthYear}
        </h2>
        
        <button
          onClick={() => navigateMonth(1)}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-[#0C5B6D]/10 flex items-center justify-center transition-colors"
          aria-label={t('myCalendar.nextMonth')}
        >
          <ChevronRight className="w-5 h-5 text-[#0C5B6D]" />
        </button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 mb-1">
        {dayNames.map((day) => (
          <div key={day} className="text-center py-1">
            <span className="font-medium text-[#0C5B6D] text-[11px] sm:text-sm">{day}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {days.map((dateObj, index) => {
          const cellDateLabel = dateFormatter.format(dateObj.date);
          const cellStateLabel = [
            dateObj.isWeekend ? t('myCalendar.weekend') : null,
            dateObj.isHoliday ? t('myCalendar.holidayWithName', { name: dateObj.holidayName }) : null,
            dateObj.isToday ? t('myCalendar.today') : null
          ].filter(Boolean).join(', ');

          return (
            <div
              key={index}
              className="aspect-square flex items-center justify-center relative"
              role="gridcell"
              aria-label={cellStateLabel ? `${cellDateLabel} - ${cellStateLabel}` : cellDateLabel}
              title={dateObj.isHoliday ? dateObj.holidayName : cellDateLabel}
            >
              <span
                className={`
                  text-sm sm:text-sm w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer
                  ${!dateObj.isCurrentMonth
                    ? 'text-gray-300'
                    : dateObj.isToday
                      ? 'bg-[#0C5B6D] text-white font-semibold'
                      : dateObj.isHoliday && dateObj.isWeekend
                        ? 'bg-amber-200 text-rose-700 ring-2 ring-rose-300'
                        : dateObj.isHoliday
                          ? 'bg-amber-200 text-amber-900'
                          : dateObj.isWeekend
                            ? 'bg-rose-100 text-rose-700'
                            : 'text-black hover:bg-[#0C5B6D]/10'
                  }
                `}
              >
                {dateObj.day}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-1">
        {legendItems.map((item) => (
          <div
            key={item.label}
            className={`w-full min-w-0 flex items-center justify-center gap-1.5 rounded-full border px-2 py-1 text-[11px] sm:text-[10px] font-medium whitespace-nowrap ${item.className}`}
          >
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyCalendar;